import React, { JSX, useContext, useState } from "react";
import Head from "next/head";
import Script from "next/script";
import Image from "next/image";
import Link from "next/link";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";
import { A11y } from "swiper/modules";
import { colors } from "constants/colors";
import { PDP_PRODUCTS } from "constants/pdpProducts";
import { assetsRoot } from "config";
import { LogContext } from "context/log";
import {
  META_TITLE,
  META_DESCRIPTION,
  CANONICAL_URL_BASE,
} from "constants/meta";
import { PAGE_TYPE_ENUM } from "@pepdirect/v3/types";
import { generatePageId } from "@pepdirect/v3/helpers/analyticsLogger";
import { HomeHero, HomeHeroContent } from "components/HomeHero";
import { CircleArrowLeft, CircleArrowRight } from "components/PdpArt";
import { SmartButton } from "components/SmartCommerce";
import { getProductUpc } from "constants/commerce";

import AmazonLogo from "components/AmazonLogo";
import st from "./home.module.scss";

const SHOP_URL = "/category/all-bundles";
const A = `${assetsRoot}/poppi`;
// New-campaign assets on the shared CDN mirror (same layout as public/ had)
const H = `${A}/home`;

// The homepage campaign hero — the sour apple launch film.
const HERO: HomeHeroContent = {
  desktopImg: `${A}/hero/brand-new-flavors.webp`,
  mobileImg: `${A}/hero/brand-new-flavors.webp`,
  videoUrl: `${A}/video/1455bd7f438c43379e6075c36b88dfd0.HD-1080p-7.2Mbps-90972926.mp4`,
  mobileVideoUrl: `${A}/video/1bfdfcb53bec4de2b26f482266819525.HD-1080p-7.2Mbps-90972925.mp4`,
  href: "/collections/drinks",
  alt: "poppi sour apple — taste the unexpected",
};

interface VarietyPack {
  name: string;
  img: string;
  color: string; // button fill
  accent: string; // button text + border
  cta?: string;
  handle?: string; // PDP handle (only packs with a product page)
}

// links to the product page when a pack has one, otherwise the shop
const packHref = (p: VarietyPack): string =>
  p.handle ? `/products/${p.handle}` : SHOP_URL;

// per-pack add-to-cart colours, matched to drinkpoppi.com
// live slide order: the seasonal Fall for Flavor pack leads (brand request
// 2026-09-02), then the current six-pack lineup (Bombshells and Soda Staples
// were dropped from the carousel)
// the seasonal pack is the one entry with a source of truth: name, theme and
// handle come from its PDP record so a brand re-theme cannot drift here
const fallForFlavor = PDP_PRODUCTS["fall-for-flavor"];

const varietyPacks: VarietyPack[] = [
  {
    name: fallForFlavor.name,
    // 800px WebP derivative of the PDP render — not fallForFlavor.cardImage,
    // which is the 7 MB hero PNG (review note on PGS-3276)
    img: `${A}/variety/fall-for-flavor.webp`,
    color: fallForFlavor.primary,
    accent: fallForFlavor.secondary,
    handle: fallForFlavor.handle,
  },
  {
    name: "Major Mix",
    img: `${A}/variety/major-mix.png`,
    color: "#8e0011",
    accent: "#f26b6b",
    handle: "major-mix",
  },
  {
    name: "Fun Favorites",
    img: `${A}/variety/functional-favorites.png`,
    color: colors.yellow,
    accent: colors.magenta,
    handle: "functional-favorites",
  },
  {
    name: "Crowd Pleasers",
    img: `${A}/variety/crowd-pleasers.png`,
    color: colors.purple,
    accent: colors.cyan,
    handle: "crowd-pleasers",
  },
  {
    name: "Classics",
    img: `${A}/variety/classics.png`,
    color: "#a40e56",
    accent: "#f66ff8",
    handle: "classics",
  },
  {
    name: "Short List",
    img: `${A}/variety/short-list.png`,
    color: colors.lime,
    accent: "#d10119",
    handle: "short-list",
  },
  {
    name: "Trend Setters",
    img: `${A}/variety/trend-setters.png`,
    color: colors.red,
    accent: colors.pink,
    handle: "trend-setters",
  },
];

// live's instagram feed strip: square tiles linking to the real posts;
// hover shows the dark layer + post-type icon, exactly like the live widget
const INSTA_POSTS: {
  href: string;
  type: "video" | "album" | "image";
  alt: string;
}[] = [
  {
    href: "https://www.instagram.com/reel/DbWcf43v3QI/",
    type: "video",
    alt: "cheers to another iconic love island season! see you next year",
  },
  {
    href: "https://www.instagram.com/p/DbWB1pcEb2Z/",
    type: "album",
    alt: "only the real poppi fans know — comment below!",
  },
  {
    href: "https://www.instagram.com/p/DbTPkREkVvo/",
    type: "album",
    alt: "be the first to try our new flavor on the @walmart site today",
  },
  {
    href: "https://www.instagram.com/reel/DbQ2gQkxWMh/",
    type: "video",
    alt: "it definitely was a poppi smash @loveisland",
  },
  {
    href: "https://www.instagram.com/p/DbQqYEGDMMK/",
    type: "album",
    alt: "who doesn’t love a good girl grip?!",
  },
  {
    href: "https://www.instagram.com/p/DbOFnFAjIZX/",
    type: "album",
    alt: "GIVEAWAY — xmas came early besties!!",
  },
  {
    href: "https://www.instagram.com/reel/DbLgxOXh7UM/",
    type: "video",
    alt: "POV: what our fridge has looked like the entire month",
  },
  {
    href: "https://www.instagram.com/reel/DbJZZDnv4-C/",
    type: "video",
    alt: "pinch us, are we dreaming?! you can now shop our iconic collection",
  },
  {
    href: "https://www.instagram.com/p/DbG9DG7PdPl/",
    type: "image",
    alt: "oh Jimothy! just keep the new flavor under wraps for now",
  },
  {
    href: "https://www.instagram.com/reel/DbGjABAxSpE/",
    type: "video",
    alt: "tiny. adorable. and our minis packs are up to 40% off!",
  },
  {
    href: "https://www.instagram.com/reel/Da8F2NmRSjB/",
    type: "video",
    alt: "treat yourself to a mini break today at @target, you deserve it!",
  },
  {
    href: "https://www.instagram.com/p/Da6AJUkPq1z/",
    type: "image",
    alt: "happy world emoji day! what’s your go-to flavor lately?!",
  },
  {
    href: "https://www.instagram.com/p/Da3APHVEbLQ/",
    type: "album",
    alt: "5 things that are worth the money?!",
  },
  {
    href: "https://www.instagram.com/reel/Da0Vlh9BdsX/",
    type: "video",
    alt: "DORM GIVEAWAY — class is almost in session",
  },
  {
    href: "https://www.instagram.com/p/DayJnXFHT5z/",
    type: "album",
    alt: "can’t stop thinking about our Casa Amor takeover",
  },
  {
    href: "https://www.instagram.com/p/Davldw5NOJz/",
    type: "image",
    alt: "how is @loveislandusa over already?! let’s give a round of applause",
  },
];

// live trimmed the marquee to four white wordmarks

const retailers: { name: string; img: string }[] = [
  { name: "Costco", img: `${A}/retailers/live/costco.png` },
  { name: "Walmart", img: `${A}/retailers/live/walmart.png` },
  { name: "Food Lion", img: `${A}/retailers/live/food-lion.png` },
  { name: "Target", img: `${A}/retailers/live/target.png` },
  // brand request: all-white amazon mark, rendered inline (no CDN asset)
  { name: "Amazon", img: "" },
];

interface LifestyleTile {
  flavor: string;
  imgs: string[];
  className: string;
  // live's floating product badge: a 60px circle over the bottom-left corner
  // that expands into the full-width pill (hover on desktop, always on mobile)
  widget?: {
    href: string;
    label: string;
    icon: string;
    primary: string;
    secondary: string;
  };
}

const lifestyleTiles: Record<string, LifestyleTile> = {
  shirleyTemple: {
    flavor: "Shirley Temple",
    imgs: [
      `${H}/carousel-a-1.jpg`,
      `${H}/carousel-a-2.jpg`,
      `${H}/carousel-a-3.jpg`,
      `${H}/carousel-a-4.jpg`,
    ],
    className: st.tileShirley,
  },
  rootBeer: {
    flavor: "Root Beer",
    imgs: [
      `${A}/lifestyle/homepage-13.jpg`,
      `${A}/lifestyle/root-beer-pdp-06.jpg`,
      `${A}/lifestyle/root-beer-pdp-02.jpg`,
      `${A}/lifestyle/root-beer-pdp-01.jpg`,
    ],
    className: st.tileRootBeer,
  },
  cherryCola: {
    flavor: "Cherry Cola",
    imgs: [
      `${A}/lifestyle/image-22.png`,
      `${A}/lifestyle/homepage-10.jpg`,
      `${A}/lifestyle/homepage-12.jpg`,
      `${A}/lifestyle/homepage-11.jpg`,
    ],
    className: st.tileCherry,
    widget: {
      href: "/products/cherry-cola",
      label: "cherry cola",
      icon: `${A}/icons/widget-cherry.png`,
      primary: "#700058",
      secondary: "#fd303a",
    },
  },
  orangeCream: {
    flavor: "Orange Cream",
    imgs: [
      `${A}/lifestyle/homepage-17.jpg`,
      `${A}/lifestyle/homepage-19.jpg`,
      `${A}/lifestyle/homepage-20.jpg`,
      `${A}/lifestyle/homepage-18.jpg`,
    ],
    className: st.tileOrange,
    widget: {
      href: "/products/orange-cream",
      label: "orange cream",
      icon: `${A}/icons/widget-orange.png`,
      primary: "#ffe6d1",
      secondary: "#e15505",
    },
  },
};

// Live renders these as a filmstrip: 450px-tall slides at their natural
// width (whole image, no cropping), each rounded, 5px apart — a draggable
// loop that only moves when pulled (no autoplay on live). Cherry cola and
// orange cream also float live's product badge over the bottom edge.
function LifestyleCard({ tile }: { tile: LifestyleTile }): JSX.Element {
  return (
    <div className={`${st.tile} ${st.stripTile} ${tile.className}`}>
      <Swiper
        className={st.stripSwiper}
        modules={[A11y]}
        slidesPerView="auto"
        spaceBetween={5}
        loop
      >
        {tile.imgs.map((img) => (
          <SwiperSlide key={img} className={st.stripSlide}>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={img}
              alt={`poppi ${tile.flavor}`}
              className={st.stripImg}
            />
          </SwiperSlide>
        ))}
      </Swiper>
      {tile.widget && (
        <div
          className={st.widgetWrap}
          style={
            {
              "--w-primary": tile.widget.primary,
              "--w-secondary": tile.widget.secondary,
            } as React.CSSProperties
          }
        >
          <Link href={tile.widget.href} className={st.widgetBtn}>
            <span className={st.widgetInner}>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={tile.widget.icon} alt="" className={st.widgetIcon} />
              <span className={st.widgetLabel}>{tile.widget.label}</span>
            </span>
          </Link>
        </div>
      )}
    </div>
  );
}

export default function Home(): JSX.Element {
  const [varietySwiper, setVarietySwiper] = useState<SwiperType | null>(null);
  const [instaSwiper, setInstaSwiper] = useState<SwiperType | null>(null);
  const { useLogPageView } = useContext(LogContext);
  useLogPageView({
    pageType: PAGE_TYPE_ENUM.home,
    pageCategory: undefined,
    pageTitle: META_TITLE,
    id: generatePageId("home"),
  });

  return (
    <div className={st.page}>
      <Script
        src="https://asset.fwcdn3.com/js/storyblock.js"
        strategy="afterInteractive"
      />
      <Head>
        <title>{META_TITLE}</title>
        <meta name="description" content={META_DESCRIPTION} key="description" />
        <link rel="canonical" href={CANONICAL_URL_BASE} />
      </Head>

      {/* ---------------- CAMPAIGN HERO (live's muted looping film) ---------------- */}
      <HomeHero data={HERO} />

      {/* ------------- BENTO GRID (live: 3 columns x 3 cells) ------------- */}
      <div className={st.grid}>
        <div className={st.gcol}>
          {/* stacked square pair: dorm collection + minis at walmart */}
          <div className={st.squarePair}>
            <a
              href="https://www.target.com/c/poppi-soda/-/N-fc6y7?type=products"
              target="_blank"
              rel="noopener noreferrer"
              className={`${st.tile} ${st.squareTile}`}
            >
              <Image
                unoptimized
                src={`${A}/tiles/dorm-collection.webp`}
                alt="poppi dorm collection"
                fill
                sizes="480px"
                style={{ objectFit: "cover" }}
              />
              <span className={st.promoBtnDorm}>available at target</span>
            </a>
            <a
              href="https://www.walmart.com/brand/poppi/10010469"
              target="_blank"
              rel="noopener noreferrer"
              className={`${st.tile} ${st.squareTile}`}
            >
              <Image
                unoptimized
                src={`${A}/tiles/rwb-minis-2026.webp`}
                alt="poppi minis — mini and major"
                fill
                sizes="480px"
                style={{ objectFit: "cover" }}
              />
              <span className={st.promoBtn}>shop now</span>
            </a>
          </div>

          {/* mixed stack: shirley temple filmstrip over the strawberry lemon
              flavor tile (replaced the green smiley per brand request 2026-09-02) */}
          <div className={st.mixedPair}>
            <div className={st.cellRest}>
              <LifestyleCard tile={lifestyleTiles.shirleyTemple} />
            </div>
            <div className={`${st.tile} ${st.squareTile} ${st.cellSquare}`}>
              <Image
                unoptimized
                src={`${H}/homepage-flavors-strawberry-lemon.webp`}
                alt="poppi strawberry lemon"
                fill
                sizes="480px"
                style={{ objectFit: "cover" }}
              />
            </div>
          </div>

          {/* mixed stack: cream-soda photo (533 tall, not a link on live)
              over the cherry-cola filmstrip with its product badge */}
          <div className={`${st.mixedPair} ${st.tallPair}`}>
            <div className={`${st.tile} ${st.squareTile} ${st.cellPhoto}`}>
              <Image
                unoptimized
                src={`${A}/tiles/creamsoda-pdp-03.jpg`}
                alt="poppi cream soda"
                fill
                sizes="480px"
                style={{ objectFit: "cover" }}
              />
            </div>
            <div className={st.cellRest}>
              <LifestyleCard tile={lifestyleTiles.cherryCola} />
            </div>
          </div>
        </div>

        <div className={st.gcol}>
          <section className={`${st.tile} ${st.varietyTile}`}>
            <h2 className={st.varietyHeading}>variety is the spice of life</h2>
            <div className={st.varietyCarousel}>
              <Swiper
                className={st.varietySwiper}
                modules={[A11y]}
                slidesPerView={1}
                spaceBetween={0}
                loop
                onSwiper={setVarietySwiper}
              >
                {varietyPacks.map((p) => (
                  <SwiperSlide key={p.name} className={st.varietySlide}>
                    <article
                      className={st.varietyCard}
                      style={
                        {
                          "--vp-primary": p.color,
                          "--vp-secondary": p.accent,
                        } as React.CSSProperties
                      }
                    >
                      <div className={st.varietyInner}>
                        <div className={st.varietyArch}>
                          <div className={st.varietyCircle}>
                            <div className={st.varietyCrop}>
                              <Link
                                href={packHref(p)}
                                className={st.varietyCanLink}
                                aria-label={`poppi ${p.name} variety pack`}
                              >
                                <img
                                  src={p.img}
                                  alt={`poppi ${p.name} variety pack`}
                                  className={st.varietyCanImg}
                                  loading="lazy"
                                />
                              </Link>
                            </div>
                          </div>
                        </div>
                        <div className={st.varietyNameBar}>
                          <Link href={packHref(p)}>
                            <h4>{p.name}</h4>
                          </Link>
                        </div>
                      </div>
                      {p.handle && getProductUpc(p.handle) && (
                        <SmartButton
                          upc={getProductUpc(p.handle) as string}
                          productName={p.name}
                          className={st.packBtn}
                          style={
                            {
                              "--color-primary": p.color,
                              "--color-secondary": p.accent,
                            } as React.CSSProperties
                          }
                        />
                      )}
                    </article>
                  </SwiperSlide>
                ))}
              </Swiper>
              <div className={st.varietyArrows}>
                <button
                  type="button"
                  className={st.varietyArrow}
                  aria-label="previous variety pack"
                  onClick={() => varietySwiper?.slidePrev()}
                >
                  <CircleArrowLeft />
                </button>
                <button
                  type="button"
                  className={st.varietyArrow}
                  aria-label="next variety pack"
                  onClick={() => varietySwiper?.slideNext()}
                >
                  <CircleArrowRight />
                </button>
              </div>
            </div>
          </section>

          <section className={`${st.tile} ${st.retailersTile}`}>
            <h2 className={st.retailersHeading}>
              we&apos;re popping up near you
            </h2>
            <div
              className={st.retailerMarquee}
              aria-label="retailers carrying poppi"
            >
              <div className={st.retailerTrack}>
                {[0, 1].map((dup) => (
                  <div
                    className={st.retailerGroup}
                    key={dup}
                    aria-hidden={dup === 1}
                  >
                    {retailers.map((r) =>
                      r.img ? (
                        <img
                          key={`${dup}-${r.name}`}
                          src={r.img}
                          alt={dup === 0 ? r.name : ""}
                          className={st.retailerLogo}
                        />
                      ) : (
                        <AmazonLogo
                          key={`${dup}-${r.name}`}
                          className={st.retailerLogo}
                          label={dup === 0 ? r.name : ""}
                        />
                      )
                    )}
                  </div>
                ))}
              </div>
            </div>
            <Link href="/pages/find-poppi" className={st.findBtn}>
              find poppi
            </Link>
            {/* live makes the whole tile clickable */}
            <Link
              href="/pages/find-poppi"
              className={st.retailersOverlay}
              aria-label="find poppi"
            />
          </section>

          <section className={`${st.tile} ${st.texasTile}`}>
            <h2 className={st.texasHeading}>once upon a time in Texas</h2>
            {/* live embeds poppi's Firework story reel (autoplaying, clickable) */}
            <div className={st.texasStory}>
              {React.createElement("fw-storyblock", {
                channel: "drinkpoppi",
                video: "5RzEDA",
                max_videos: "1",
                autoplay: "true",
                branding: "false",
                style: { height: "75vh", width: "100%" },
              })}
            </div>
            <Link href="/pages/our-story" className={st.texasBtn}>
              our story
            </Link>
          </section>
        </div>

        <div className={st.gcol}>
          {/* mixed stack: the "save water, drink soda" art tile (replaced the
              spider-man movie-rewards tile per brand request 2026-09-02) over
              the winking-smiley photo tile (live's 1020_2025_HOMEPAGEUPDATE-22_1.jpg) */}
          <div className={st.mixedPair}>
            <div
              className={`${st.tile} ${st.squareTile} ${st.cellSquare}`}
              // the artwork is near-square with lettering to the edges: letterbox
              // it on its own purple rather than crop the type
              style={{ backgroundColor: "#450099" }}
            >
              <Image
                unoptimized
                src={`${H}/save-water-drink-soda.webp`}
                alt="save water, drink soda"
                fill
                sizes="480px"
                style={{ objectFit: "contain" }}
              />
            </div>
            <div className={st.cellRest}>
              <div className={st.tile}>
                <Image
                  unoptimized
                  src={`${H}/tile-smiley-wink.jpg`}
                  alt=""
                  fill
                  sizes="480px"
                  style={{ objectFit: "cover" }}
                />
              </div>
            </div>
          </div>

          {/* mixed stack: root beer carousel over the wild berry flavor tile
              (replaced the "looking juicy" square per brand request 2026-09-02;
              the tile is not a link, like live) */}
          <div className={st.mixedPair}>
            <div className={st.cellRest}>
              <LifestyleCard tile={lifestyleTiles.rootBeer} />
            </div>
            <div className={`${st.tile} ${st.squareTile} ${st.cellSquare}`}>
              <Image
                unoptimized
                src={`${H}/homepage-flavors-wild-berry.webp`}
                alt="poppi wild berry"
                fill
                sizes="480px"
                style={{ objectFit: "cover" }}
              />
            </div>
          </div>

          {/* mixed stack: stacked-cans photo (533 tall, not a link on live)
              over the orange-cream filmstrip with its product badge */}
          <div className={`${st.mixedPair} ${st.tallPair}`}>
            <div className={`${st.tile} ${st.squareTile} ${st.cellPhoto}`}>
              <Image
                unoptimized
                src={`${A}/pdp/gallery/major-mix-07.jpg`}
                alt="poppi cans stacked"
                fill
                sizes="480px"
                style={{ objectFit: "cover" }}
              />
            </div>
            <div className={st.cellRest}>
              <LifestyleCard tile={lifestyleTiles.orangeCream} />
            </div>
          </div>
        </div>
      </div>

      {/* ---------------- instagram feed strip (live's instafeed widget) ---------------- */}
      <div className={st.instaRow}>
        <Swiper
          className={st.instaSwiper}
          modules={[A11y]}
          slidesPerView={3}
          slidesPerGroup={2}
          spaceBetween={2.4}
          breakpoints={{
            768: { slidesPerView: 5, slidesPerGroup: 2 },
            1024: { slidesPerView: 8, slidesPerGroup: 2 },
          }}
          onSwiper={setInstaSwiper}
        >
          {INSTA_POSTS.map((post, i) => (
            <SwiperSlide key={post.href}>
              <a
                href={post.href}
                target="_blank"
                rel="noopener nofollow noreferrer"
                className={st.instaTile}
              >
                <img
                  src={`${A}/ugc/live/post-${i + 1}.jpg`}
                  alt={`Instagram post with the caption: ${post.alt}`}
                  className={st.instaImg}
                  loading="lazy"
                />
                <span className={st.instaHover}>
                  <img
                    src={
                      post.type === "video"
                        ? `${A}/ugc/hover-video.png`
                        : `${A}/ugc/hover-instagram.svg`
                    }
                    alt=""
                  />
                </span>
              </a>
            </SwiperSlide>
          ))}
        </Swiper>
        <button
          type="button"
          className={`${st.instaArrow} ${st.instaArrowPrev}`}
          aria-label="previous posts"
          onClick={() => instaSwiper?.slidePrev()}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M15 4l-8 8 8 8"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <button
          type="button"
          className={`${st.instaArrow} ${st.instaArrowNext}`}
          aria-label="more posts"
          onClick={() => instaSwiper?.slideNext()}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M9 4l8 8-8 8"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}
